import * as Immutable from "immutable";
import React from "react";
import * as AF from "src/types/formTemplate";
import * as S from "./types/state";

export type Action =
  | {
      type: "AddQuestion";
      sectionId: string;
      atIndex: number;
    }
  | {
      type: "RemoveQuestion";
      sectionId: string;
      question: AF.V2.Question;
    }
  | {
      type: "ArchiveQuestion";
      sectionId: string;
      question: AF.V2.Question;
    }
  | {
      type: "UnarchiveQuestion";
      sectionId: string;
      question: AF.V2.Question;
    }
  | {
      type: "UpdateQuestion";
      sectionId: string;
      question: AF.V2.Question;
    }
  | {
      type: "AddSection";
      section: AF.V2.Section;
      atIndex: number;
    }
  | {
      type: "UpdateSection";
      section: AF.V2.Section;
    }
  | {
      type: "RemoveSection";
      section: AF.V2.Section;
    }
  | {
      type: "AddVerification";
      verification: AF.V2.FormVerification;
    }
  | {
      type: "RemoveVerifications";
      verificationIds: string[];
    }
  | {
      type: "UpdateVerification";
      verification: AF.V2.FormVerification;
    }
  | {
      type: "UpdateVerification";
      verification: AF.V2.FormVerification;
    }
  | {
      type: "SetFormTemplate";
      formTemplate: AF.V2.FormTemplate;
    }
  | {
      type: "MoveSection";
      sectionId: string;
      beforeSectionId: string;
    }
  | {
      type: "MoveQuestion";
      questionId: string;
      beforeQuestionId: string;
    }
  | {
      type: "AppendQuestionToSection";
      questionId: string;
      sectionId: string;
    }
  | {
      type: "SetValidations";
      validations: AF.V2.ValidationResult[];
    }
  | {
      type: "DismissValidation";
      ids: string[];
    }
  | {
      type: "SetImportErrors";
      errors: AF.V2.FormTemplateImportError[];
    }
  | {
      type: "DismissImportErrors";
      ids: string[];
    }
  | {
      type: "SetQuestionMode";
      questionId: string;
      mode: "Edit" | "View";
    }
  | {
      type: "SetDiff";
      diff: AF.V2.FormTemplateSimpleDiff;
    }
  | {
      type: "ClearUnsavedChanges";
    };

export function reducer(state: S.State, action: Action): S.State {
  switch (action.type) {
    case "AddQuestion":
      state = addQuestion(state, action);
      return state;

    case "UpdateQuestion":
      state = updateQuestion(state, action);
      state = dismissValidation(state, { ids: [action.question.id] });
      state = dismissImportError(state, { ids: [action.question.id] });
      return state;

    case "RemoveQuestion":
      state = removeQuestion(state, action);
      state = dismissValidation(state, {
        ids: [action.question.id, action.sectionId],
      });
      state = dismissImportError(state, {
        ids: [action.question.id, action.sectionId],
      });
      return state;

    case "ArchiveQuestion":
      state = archiveQuestion(state, action);
      state = dismissValidation(state, { ids: [action.question.id] });
      state = dismissImportError(state, { ids: [action.question.id] });
      return state;

    case "UnarchiveQuestion":
      state = unarchiveQuestion(state, action);
      state = dismissValidation(state, { ids: [action.question.id] });
      state = dismissImportError(state, { ids: [action.question.id] });
      return state;

    case "AddSection":
      state = addSection(state, action);
      state = dismissValidation(state, { ids: [state.get("draft").id] });
      state = dismissImportError(state, { ids: [state.get("draft").id] });
      return state;

    case "UpdateSection":
      state = updateSection(state, action);
      state = dismissValidation(state, { ids: [action.section.id] });
      state = dismissImportError(state, { ids: [action.section.id] });
      return state;

    case "RemoveSection":
      state = removeSection(state, action);
      state = dismissValidation(state, { ids: [action.section.id] });
      state = dismissImportError(state, { ids: [action.section.id] });
      return state;

    case "AddVerification":
      return addVerification(state, action);

    case "RemoveVerifications":
      return removeVerifications(state, action);

    case "UpdateVerification":
      return updateVerification(state, action);

    case "SetFormTemplate": {
      return S.NewState({
        draft: action.formTemplate,
        original: action.formTemplate,
        importErrors: state.get("importErrors"),
        diff: state.get("diff"),
      });
    }

    case "MoveSection":
      state = moveSection(state, action);
      state = dismissValidation(state, {
        ids: [action.beforeSectionId, action.sectionId],
      });
      state = dismissImportError(state, {
        ids: [action.beforeSectionId, action.sectionId],
      });
      return state;

    case "MoveQuestion":
      state = moveQuestion(state, action);
      state = dismissValidation(state, {
        ids: [action.questionId, action.beforeQuestionId],
      });
      state = dismissImportError(state, {
        ids: [action.questionId, action.beforeQuestionId],
      });
      return state;

    case "AppendQuestionToSection":
      state = appendQuestionToSection(state, action);
      state = dismissValidation(state, {
        ids: [action.questionId, action.sectionId],
      });
      state = dismissImportError(state, {
        ids: [action.questionId, action.sectionId],
      });
      return state;

    case "SetValidations":
      return setValidations(state, action);

    case "DismissValidation":
      return dismissValidation(state, action);

    case "SetImportErrors":
      return setImportErrors(state, action);

    case "DismissImportErrors":
      return dismissImportError(state, action);

    case "SetQuestionMode":
      return setQuestionMode(state, action);

    case "SetDiff":
      return state.set("diff", action.diff);

    case "ClearUnsavedChanges":
      return state.set("draft", state.get("original"));

    default:
      const _exhaustiveCheck: never = action;
      return _exhaustiveCheck;
  }
}

function dismissImportError(
  state: S.State,
  params: {
    ids: string[];
  }
) {
  return state.update("importErrors", (importErrors) => {
    for (const id of params.ids) {
      importErrors = importErrors.remove(id);
    }

    return importErrors;
  });
}

function setImportErrors(
  state: S.State,
  params: {
    errors: AF.V2.FormTemplateImportError[];
  }
): S.State {
  let importErrorsMap = Immutable.Map<string, Immutable.List<string>>();

  const formTemplateId = state.get("draft").id;
  for (const error of params.errors) {
    const id = "id" in error ? error.id ?? formTemplateId : formTemplateId;
    const existingErrors = importErrorsMap.get(id, Immutable.List<string>());

    const updatedErrors = existingErrors.push(error.error);
    importErrorsMap = importErrorsMap.set(id, updatedErrors);
  }

  return state.set("importErrors", importErrorsMap);
}

function dismissValidation(
  state: S.State,
  params: {
    ids: string[];
  }
) {
  return state.update("validations", (validations) => {
    for (const id of params.ids) {
      validations = validations.remove(id);
    }

    return validations;
  });
}

function setValidations(
  state: S.State,
  params: {
    validations: AF.V2.ValidationResult[];
  }
) {
  let validationsMap = Immutable.Map<string, Immutable.List<string>>();

  const formTemplateId = state.get("draft").id;
  for (const validation of params.validations) {
    const id =
      "id" in validation ? validation.id ?? formTemplateId : formTemplateId;
    const existingErrors = validationsMap.get(id, Immutable.List<string>());

    const updatedErrors = existingErrors.push(validation.error);
    validationsMap = validationsMap.set(id, updatedErrors);
  }

  return state.set("validations", validationsMap);
}

function appendQuestionToSection(
  state: S.State,
  params: {
    questionId: string;
    sectionId: string;
  }
) {
  return state.update("draft", (draft) => {
    const questionToMove = findQuestion(params.questionId, draft);
    if (!questionToMove) {
      return draft;
    }

    let sectionFound = false;
    const sections = [];
    for (const section of draft.sections) {
      if (!("questions" in section)) {
        sections.push(section);
        continue;
      }

      let questions = [];

      if (section.id === params.sectionId) {
        sectionFound = true;
        questions = [
          ...section.questions.filter((q) => q.id !== questionToMove.id),
          questionToMove,
        ];
      } else {
        for (const question of section.questions) {
          if (question.id === params.questionId) {
            // Remove the question from its original location
            continue;
          } else {
            questions.push(question);
          }
        }
      }

      sections.push({ ...section, questions });
    }

    if (!sectionFound) {
      // target question not found. Do nothing
      return draft;
    }

    return {
      ...draft,
      sections,
    };
  });
}

function moveQuestion(
  state: S.State,
  params: {
    questionId: string;
    beforeQuestionId: string;
  }
) {
  return state.update("draft", (draft) => {
    const questionToMove = findQuestion(params.questionId, draft);
    if (!questionToMove) {
      return draft;
    }

    let beforeQuestionFound = false;
    const sections = [];
    for (const section of draft.sections) {
      if (!("questions" in section)) {
        sections.push(section);
        continue;
      }

      const questions = [];
      for (const question of section.questions) {
        if (question.id === params.questionId) {
          // Remove the question from its original location
          continue;
        } else if (question.id === params.beforeQuestionId) {
          beforeQuestionFound = true;
          // Add question to new location
          questions.push(questionToMove);
          questions.push(question);
        } else {
          questions.push(question);
        }
      }

      sections.push({ ...section, questions });
    }

    if (!beforeQuestionFound) {
      // target question not found. Do nothing
      return draft;
    }

    return {
      ...draft,
      sections,
    };
  });
}

function findQuestion(
  questionId: string,
  formTemplate: AF.V2.FormTemplate
): AF.V2.Question | undefined {
  for (const section of formTemplate.sections) {
    if (!("questions" in section)) {
      continue;
    }

    for (const question of section.questions) {
      if (question.id === questionId) {
        return question;
      }
    }
  }

  return undefined;
}

function moveSection(
  state: S.State,
  params: {
    sectionId: string;
    beforeSectionId: string;
  }
) {
  return state.update("draft", (draft) => {
    const sections = draft.sections;
    const updatedSections = sections.filter((s) => s.id !== params.sectionId);
    const sectionToMove = sections.find((s) => s.id === params.sectionId);
    if (sectionToMove === undefined) {
      return draft;
    }

    const index = updatedSections.findIndex(
      (s) => s.id === params.beforeSectionId
    );
    updatedSections.splice(index, 0, sectionToMove);
    return {
      ...draft,
      sections: updatedSections,
    };
  });
}

export function addSection(
  state: S.State,
  params: {
    section: AF.V2.Section;
    atIndex: number;
  }
) {
  const { section, atIndex } = params;

  return state.update("draft", (draft) => ({
    ...draft,
    sections: [
      ...draft.sections.slice(0, atIndex),
      section,
      ...draft.sections.slice(atIndex),
    ],
  }));
}

function addVerification(
  state: S.State,
  params: { verification: AF.V2.FormVerification }
) {
  const { verification } = params;

  return state.update("draft", (draft) => ({
    ...draft,
    verifications: [...(draft.verifications ?? []), verification],
  }));
}

function removeVerifications(
  state: S.State,
  params: { verificationIds: string[] }
) {
  const { verificationIds } = params;

  if (verificationIds.length === 0) {
    return state;
  }

  return state.update("draft", (draft) => ({
    ...draft,
    sections: draft.sections.map((section) =>
      section.type === "GeneralSection" || section.type === "PreRankingSection"
        ? {
            ...section,
            questions: section.questions.map((question) =>
              removeVerificationFromQuestion(question, verificationIds)
            ),
          }
        : section
    ),
    verifications: draft.verifications?.filter(
      (v) => !verificationIds.includes(v.id)
    ),
  }));
}

function removeVerificationFromQuestion(
  question: AF.V2.Question,
  verificationIds: string[]
) {
  if (
    "formVerificationId" in question &&
    question.formVerificationId &&
    verificationIds.includes(question.formVerificationId)
  ) {
    return {
      ...question,
      formVerificationId: undefined,
    };
  }
  return question;
}

function updateVerification(
  state: S.State,
  params: { verification: AF.V2.FormVerification }
) {
  const { verification } = params;

  // performs an upsert
  return state.update("draft", (draft) => ({
    ...draft,
    verifications: (() => {
      const existing = draft.verifications?.find(
        (v) => v.id === verification.id
      );
      if (existing) {
        return draft.verifications?.map((v) =>
          v.id === verification.id ? { ...v, ...verification } : v
        );
      } else {
        return [...(draft.verifications ?? []), verification];
      }
    })(),
  }));
}

function updateSection(
  state: S.State,
  params: { section: AF.V2.Section }
): S.State {
  const { section } = params;
  return state.update("draft", (draft) => ({
    ...draft,
    sections: draft.sections.map((s) => (s.id === section.id ? section : s)),
  }));
}

function removeSection(
  state: S.State,
  params: { section: AF.V2.Section }
): S.State {
  const { section } = params;
  const sectionId = section.id;
  return state.update("draft", (draft) => ({
    ...draft,
    sections: draft.sections.filter((s) => s.id !== sectionId),
  }));
}

function addQuestion(
  state: S.State,
  params: {
    sectionId: string;
    atIndex: number;
  }
) {
  const { sectionId, atIndex } = params;
  const draft = state.get("draft");
  const section = findSection(draft, sectionId);
  const question: AF.V2.Question = {
    id: window.crypto.randomUUID(),
    key: "",
    type: "FreeText",
    question: "",
  };

  if (!section) {
    throw new Error(`Question cannot be added. Section ${sectionId} not found`);
  }

  if (
    section.type !== "GeneralSection" &&
    section.type !== "PreRankingSection"
  ) {
    throw new Error(
      `Question cannot be added. Section ${sectionId} is not a general or pre-ranking section`
    );
  }

  const questions = section.questions;

  const newQuestions = [
    ...questions.slice(0, atIndex),
    question,
    ...questions.slice(atIndex),
  ] satisfies AF.V2.Question[];

  return state
    .update("draft", (draft) => ({
      ...draft,
      sections: draft.sections.map((section) =>
        section.id === sectionId
          ? { ...section, questions: newQuestions }
          : section
      ),
    }))
    .set("questionModes", state.get("questionModes").set(question.id, "Edit"));
}

function updateQuestion(
  state: S.State,
  params: {
    sectionId: string;
    question: AF.V2.Question;
  }
): S.State {
  const { sectionId, question } = params;
  const draft = state.get("draft");
  const section = findSection(draft, sectionId);

  if (!section) {
    throw new Error(
      `Question cannot be updated. Section ${sectionId} not found`
    );
  }

  if (
    section.type !== "GeneralSection" &&
    section.type !== "PreRankingSection"
  ) {
    throw new Error(
      `Question cannot be updated. Section ${sectionId} is not a general or pre-ranking section`
    );
  }

  const updatedSection = {
    ...section,
    questions: section.questions.map((q) => {
      if (q.id !== question.id) {
        return q;
      }

      return question;
    }),
  };

  return state.update("draft", (draft) => ({
    ...draft,
    sections: draft.sections.map((section) =>
      section.id === sectionId ? updatedSection : section
    ),
  }));
}

function removeQuestion(
  state: S.State,
  params: {
    sectionId: string;
    question: AF.V2.Question;
  }
) {
  const { sectionId, question } = params;
  const draft = state.get("draft");
  const section = findSection(draft, sectionId);

  if (!section) {
    throw new Error(
      `Question cannot be updated. Section ${sectionId} not found`
    );
  }

  if (
    section.type !== "GeneralSection" &&
    section.type !== "PreRankingSection"
  ) {
    throw new Error(
      `Question cannot be updated. Section ${sectionId} is not a general or pre-ranking section`
    );
  }

  const updatedSection = {
    ...section,
    questions: section.questions.filter((q) => q.id !== question.id),
  };

  return state.update("draft", (draft) => ({
    ...draft,
    sections: draft.sections.map((section) =>
      section.id === sectionId ? updatedSection : section
    ),
  }));
}

function archiveQuestion(
  state: S.State,
  params: {
    sectionId: string;
    question: AF.V2.Question;
  }
) {
  const { sectionId, question } = params;
  const draft = state.get("draft");
  const section = findSection(draft, sectionId);

  if (!section) {
    throw new Error(
      `Question cannot be archived. Section ${sectionId} not found`
    );
  }

  if (
    section.type !== "GeneralSection" &&
    section.type !== "PreRankingSection"
  ) {
    throw new Error(
      `Question cannot be archived. Section ${sectionId} is not a general or pre-ranking section`
    );
  }

  // Create archived version of the question
  let archivedQuestion: AF.V2.Question = {
    ...question,
    isArchived: true,
    requirement: undefined, // Remove requirement when archiving
    visibility: undefined, // Remove visibility when archiving
  };

  // Remove exclude school logic from all options when archiving a question
  if ("options" in question) {
    archivedQuestion = {
      ...archivedQuestion,
      options: question.options.map((option) => {
        if ("notEligibleSchoolIds" in option) {
          const { eligibilityFilter, notEligibleSchoolIds, ...rest } = option;
          return rest;
        }
        const { eligibilityFilter, ...rest } = option;
        return rest;
      }),
    } as AF.V2.Question;
  }

  const updatedSection = {
    ...section,
    questions: section.questions.map((q) => {
      if (q.id !== question.id) {
        return q;
      }
      return archivedQuestion;
    }),
  };

  return state.update("draft", (draft) => ({
    ...draft,
    sections: draft.sections.map((section) =>
      section.id === sectionId ? updatedSection : section
    ),
  }));
}

function unarchiveQuestion(
  state: S.State,
  params: {
    sectionId: string;
    question: AF.V2.Question;
  }
) {
  const { sectionId, question } = params;
  const draft = state.get("draft");
  const section = findSection(draft, sectionId);

  if (!section) {
    throw new Error(
      `Question cannot be unarchived. Section ${sectionId} not found`
    );
  }

  if (
    section.type !== "GeneralSection" &&
    section.type !== "PreRankingSection"
  ) {
    throw new Error(
      `Question cannot be unarchived. Section ${sectionId} is not a general or pre-ranking section`
    );
  }

  // Create unarchived version of the question
  const unarchivedQuestion = {
    ...question,
    isArchived: false,
  };

  const updatedSection = {
    ...section,
    questions: section.questions.map((q) => {
      if (q.id !== question.id) {
        return q;
      }
      return unarchivedQuestion;
    }),
  };

  return state.update("draft", (draft) => ({
    ...draft,
    sections: draft.sections.map((section) =>
      section.id === sectionId ? updatedSection : section
    ),
  }));
}

function findSection(
  draft: AF.V2.FormTemplate,
  sectionId: string
): AF.V2.Section | undefined {
  return draft.sections.find((section) => section.id === sectionId);
}

function setQuestionMode(
  state: S.State,
  params: {
    questionId: string;
    mode: "Edit" | "View";
  }
): S.State {
  return state.set(
    "questionModes",
    state.get("questionModes").set(params.questionId, params.mode)
  );
}

function toState(
  formTemplate: AF.V2.FormTemplate,
  diff?: AF.V2.FormTemplateSimpleDiff
): S.State {
  return S.NewState({
    original: formTemplate,
    draft: formTemplate,
    diff,
  });
}

export type DispatchFn = React.Dispatch<Action>;

export function useFormTemplateReducer(
  formTemplate: AF.V2.FormTemplate,
  diff?: AF.V2.FormTemplateSimpleDiff
) {
  const initialState = toState(formTemplate, diff);
  const [state, dispatch] = React.useReducer(reducer, initialState);

  return { state, dispatch };
}
